import { DiffRatioDisplay } from "@/components/dashboard/diff-ratio-display";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardHeader,
  Image,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from "@backpac/monica";
import ChartPlaceholder from "~/assets/chart-placeholder.svg";

type AnalyticCardProps = {
  className?: string;
  title: string;
  description: string;
  value: string;
  diffRatio?: number | null;
  children: React.ReactNode;
};

// To prevent the tooltip from being cut off, apply a negative margin-right to the container and a positive padding-right to the container.
const YAXIS_RIGHT_OFFSET = 16;

export function AnalyticsCard({ className, title, description, value, diffRatio, children }: AnalyticCardProps) {
  return (
    <Card className={cn("space-y-3 h-fit shadow-standard", className)}>
      <CardHeader className="pb-0">
        <div>
          <Tooltip>
            <TooltipTrigger>
              <Typography
                size="sm"
                className="text-left w-fit underline decoration-dotted decoration-[1.5px] underline-offset-[3.75px] [text-decoration-skip-ink:none] [text-underline-position:from-font]"
              >
                {title}
              </Typography>
            </TooltipTrigger>
            <TooltipContent position="topMiddle" side="bottom" className="flex flex-col gap-1 text-left">
              <Typography size="sm" weight={600} color="white">
                {title}
              </Typography>
              {description}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center justify-between gap-1">
          <Typography size="xl" weight={600}>
            {value}
          </Typography>
          {diffRatio != null && <DiffRatioDisplay diffRatio={diffRatio} />}
        </div>
      </CardHeader>
      {/* To control the left spacing of the Y-axis label, apply a negative margin-left. */}
      <CardContent className={`ml-[-16px] mr-[-${YAXIS_RIGHT_OFFSET}px]`}>{children}</CardContent>
    </Card>
  );
}

AnalyticsCard.Pending = () => {
  return (
    <Card className="space-y-3 h-fit">
      <CardHeader className="pb-0">
        <Skeleton.Base width={120} height={18} />
        <div className="flex items-center justify-between gap-1">
          <Skeleton.Base width={60} height={30} />
        </div>
      </CardHeader>
      <CardContent>
        <Image src={ChartPlaceholder} alt="Chart Placeholder" className="w-full" />
      </CardContent>
    </Card>
  );
};

AnalyticsCard.NotAvailable = ({ title, description }: Omit<AnalyticCardProps, "children" | "value">) => {
  return (
    <Card className="space-y-3 h-fit shadow-standard">
      <CardHeader className="pb-0">
        <div>
          <Tooltip>
            <TooltipTrigger>
              <Typography
                size="sm"
                className="text-left w-fit underline decoration-dotted decoration-[1.5px] underline-offset-[3.75px] [text-decoration-skip-ink:none] [text-underline-position:from-font]"
              >
                {title}
              </Typography>
            </TooltipTrigger>
            <TooltipContent position="topMiddle" side="bottom" className="flex flex-col gap-1 text-left">
              <Typography size="sm" weight={600} color="white">
                {title}
              </Typography>
              {description}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center justify-between gap-1">
          <Typography size="xl" weight={600} className="leading-[1.25]">
            N/A
          </Typography>
        </div>
      </CardHeader>
      <CardContent>
        <Image src={ChartPlaceholder} alt="Chart Placeholder" className="w-full" />
      </CardContent>
    </Card>
  );
};
